import { forwardRef } from 'react'

import { useRender } from '@base-ui/react/use-render'
import { tv, type VariantProps } from 'tailwind-variants'

import { cn } from '../../../utils/cn'
import { Icon } from '../icon/icon'

const trendIndicatorVariants = tv({
  base: 'font-body inline-flex items-center gap-1 whitespace-nowrap font-semibold',
  variants: {
    sentiment: {
      positive: 'text-success-body',
      negative: 'text-danger-body',
      neutral: 'text-surface-body'
    },
    variant: {
      inline: '',
      pill: 'rounded-md px-1.5 py-0.5'
    },
    size: {
      sm: 'text-xs',
      md: 'text-sm'
    }
  },
  compoundVariants: [
    { variant: 'pill', sentiment: 'positive', class: 'bg-success-bg' },
    { variant: 'pill', sentiment: 'negative', class: 'bg-danger-bg' },
    { variant: 'pill', sentiment: 'neutral', class: 'bg-surface-200' }
  ],
  defaultVariants: { sentiment: 'positive', variant: 'inline', size: 'sm' }
})

export type TrendDirection = 'up' | 'down' | 'flat'

/** `outward` = diagonal ↗ / ↘; `cardinal` = straight ↑ / ↓; `flat` = → (no change). */
export type TrendArrow = 'outward' | 'cardinal'

interface ArrowIcon {
  name: string
  /** Optional rotation so a single glyph can serve two directions. */
  className?: string
}

const ARROW_ICONS: Record<TrendArrow, Record<TrendDirection, ArrowIcon>> = {
  outward: {
    up: { name: 'arrow_outward' },
    // Reuse the diagonal `arrow_outward` glyph rotated 90° (↗ → ↘) so up/down
    // read as a matched diagonal pair instead of pairing a diagonal up with a
    // straight-down arrow. Keeps the icon subset to a single glyph, too.
    down: { name: 'arrow_outward', className: 'rotate-90' },
    flat: { name: 'arrow_right_alt' }
  },
  cardinal: {
    up: { name: 'arrow_upward_alt' },
    down: { name: 'arrow_downward_alt' },
    flat: { name: 'arrow_right_alt' }
  }
}

const DIRECTION_SR_LABEL: Record<TrendDirection, string> = {
  up: 'Increased',
  down: 'Decreased',
  flat: 'No change'
}

export type TrendIndicatorProps = Omit<
  React.ComponentPropsWithRef<'span'>,
  'children'
> &
  VariantProps<typeof trendIndicatorVariants> & {
    direction: TrendDirection
    arrow?: TrendArrow
    value: React.ReactNode
    label?: string
    render?: useRender.RenderProp
  }

export const TrendIndicator = forwardRef<HTMLSpanElement, TrendIndicatorProps>(
  (
    {
      direction,
      arrow = 'outward',
      sentiment,
      variant,
      size,
      value,
      label,
      className,
      render,
      ...props
    },
    ref
  ) =>
    useRender({
      render,
      ref,
      defaultTagName: 'span',
      props: {
        'data-slot': 'trend-indicator',
        'data-direction': direction,
        className: cn(
          trendIndicatorVariants({ sentiment, variant, size }),
          className
        ),
        children: (
          <>
            <Icon
              name={ARROW_ICONS[arrow][direction].name}
              size='sm'
              aria-hidden
              className={ARROW_ICONS[arrow][direction].className}
            />
            <span className='sr-only'>{DIRECTION_SR_LABEL[direction]}</span>
            <span>{value}</span>
            {label ? (
              <span className='text-surface-body font-normal'>{label}</span>
            ) : null}
          </>
        ),
        ...props
      }
    })
)
TrendIndicator.displayName = 'TrendIndicator'
